export interface ShipperSummaryItem {
  shipperName: string;
  totalShipments: number;
  totalQuantity: number;
  totalWeight: number;
  totalVolume: number;
}

export interface ShipperSummaryTotals {
  shipmentOrderCount: number;
  quantityTotal: number;
  totalWeightKg: number;
  totalVolumeM3: number;
  assignedVehicleCount: number;
}

export interface ShipperSummaryPrintOptions {
  startDate: string;
  endDate: string;
  totals?: ShipperSummaryTotals;
}

const escapeHtml = (value: string): string => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

export const formatSummaryValue = (value: number, suffix = ''): string => `${Number(value || 0).toLocaleString()}${suffix}`;

const formatDateRange = (startDate: string, endDate: string): string => (
  startDate === endDate ? startDate : `${startDate} 〜 ${endDate}`
);

const buildEmptyRow = (): string => (
  '<tr><td colspan="5" class="empty">表示できる集計がありません。</td></tr>'
);

const buildRow = (item: ShipperSummaryItem): string => (
  `<tr>
    <td>${escapeHtml(item.shipperName)}</td>
    <td class="num">${item.totalShipments.toLocaleString()}</td>
    <td class="num">${item.totalQuantity.toLocaleString()}</td>
    <td class="num">${formatSummaryValue(item.totalWeight, 'kg')}</td>
    <td class="num">${formatSummaryValue(item.totalVolume, '㎥')}</td>
  </tr>`
);

const buildTotalsSection = (totals: ShipperSummaryTotals): string => (
  `<section class="totals">
    <h2>全荷主合計</h2>
    <table>
      <thead>
        <tr>
          <th>伝票数合計</th>
          <th>数量合計</th>
          <th>重量合計</th>
          <th>体積合計</th>
          <th>配車済み車両台数</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="num">${totals.shipmentOrderCount.toLocaleString()}</td>
          <td class="num">${totals.quantityTotal.toLocaleString()}</td>
          <td class="num">${formatSummaryValue(totals.totalWeightKg, 'kg')}</td>
          <td class="num">${formatSummaryValue(totals.totalVolumeM3, '㎥')}</td>
          <td class="num">${totals.assignedVehicleCount.toLocaleString()}</td>
        </tr>
      </tbody>
    </table>
  </section>`
);

export const buildShipperSummaryPrintHtml = (
  items: ShipperSummaryItem[],
  { startDate, endDate, totals }: ShipperSummaryPrintOptions,
): string => {
  const rows = items.length === 0
    ? buildEmptyRow()
    : items.map(buildRow).join('\n');

  const totalsHtml = totals ? buildTotalsSection(totals) : '';

  return `<!DOCTYPE html>
<html lang="ja">
<head>
  <meta charset="utf-8" />
  <title>荷主別集計</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", "Hiragino Sans", "Yu Gothic", "Meiryo", sans-serif;
      color: #000;
      margin: 16mm;
    }
    h1 {
      font-size: 18px;
      margin: 0 0 4px;
    }
    h2 {
      font-size: 14px;
      margin: 12px 0 4px;
    }
    .period {
      font-size: 12px;
      color: #333;
      margin: 0 0 12px;
    }
    .totals {
      margin: 0 0 12px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
    }
    th, td {
      border: 1px solid #333;
      padding: 4px 8px;
      vertical-align: middle;
    }
    th {
      background: #f0f0f0;
      text-align: center;
    }
    td.num {
      text-align: right;
    }
    td.empty {
      text-align: center;
      color: #666;
    }
    @page {
      size: A4;
      margin: 12mm;
    }
  </style>
</head>
<body>
  <h1>荷主別集計</h1>
  <p class="period">対象期間: ${escapeHtml(formatDateRange(startDate, endDate))}</p>
  ${totalsHtml}
  <table>
    <thead>
      <tr>
        <th>荷主名</th>
        <th>伝票数</th>
        <th>数量合計</th>
        <th>重量合計</th>
        <th>体積合計</th>
      </tr>
    </thead>
    <tbody>
      ${rows}
    </tbody>
  </table>
  <script>
    window.addEventListener('load', () => {
      window.focus();
      window.print();
    });
  </script>
</body>
</html>`;
};
